/*
 * A file handed to the analysis workbench by another page (Home's image
 * source picker, components/hub/ImageSourcePicker.js), across one in-app
 * navigation.
 *
 * A File cannot go into the address, and history state is copied with the
 * structured-clone algorithm, which would keep it on some browsers and not on
 * others and would bring it back on every reload of that entry. So it is kept
 * here, in this module's memory and nowhere else -- never in storage -- and the
 * navigation carries only its token (state.handoff). The workbench takes it
 * once (pages/tools/AnalysisWorkbench.js): Back to that entry, or a reload,
 * which empties this memory, finds nothing and opens the workbench as it is.
 *
 *   const token = handOff(file, { mode: 'culture' });
 *   navigate(path, { state: { returnTo, handoff: token } });
 *   ...
 *   takeHandOff(token)   // {file, meta} the first time, null after
 */
const waiting = new Map();
let counter = 0;

// Files handed over and never taken (a navigation refused, the page left before it loaded) are let go
// past this many, the oldest first: a handful of pictures, never a growing pile.
const KEEP = 4;

/** An unguessable token: the browser's random UUID, else a counter with the time and a random part. */
function newToken() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* an insecure origin: no randomUUID */
  }
  counter += 1;
  return `h${Date.now().toString(36)}-${counter.toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/** Keeps `file` for the workbench; the token to put in the navigation's state. */
export function handOff(file, meta = {}) {
  const token = newToken();
  waiting.set(token, { file, meta: { ...meta } });
  while (waiting.size > KEEP) waiting.delete(waiting.keys().next().value);
  return token;
}

/** The file handed over under `token`, once: {file, meta}, then null (and null for a token never given). */
export function takeHandOff(token) {
  if (typeof token !== 'string' || !waiting.has(token)) return null;
  const entry = waiting.get(token);
  waiting.delete(token);
  return entry;
}
